if (!customElements.get('help-single-page')) {
  customElements.define(
    'help-single-page',
    class HelpSinglePage extends HTMLElement {
      constructor() {
        super();

        this.nav = this.querySelector('[js-help-single-nav]');
        this.mobileNav = this.querySelector('[js-help-single-mobile-links]');
        this.content = this.querySelector('[js-help-single-content]');
        this.sections = [];
        this.navItems = [];
        this.isScrolling = false;
        this.scrollTimeout = null;

        this.init();
      }

      init() {
        this.extractTOC();
        this.setupScrollObserver();
        this.handleInitialHash();
      }

      extractTOC() {
        if (!this.content || !this.nav) return;

        // Find the inline TOC nav element
        const inlineTOC = this.content.querySelector('nav[aria-label="Table of contents"]');
        if (!inlineTOC) {
          // Fallback: find any nav with a ul containing anchor links
          const fallbackNav = this.content.querySelector('nav ul');
          if (fallbackNav) {
            this.buildNavFromLinks(fallbackNav.querySelectorAll('a[href^="#"]'));
          }
          return;
        }

        // Get all links from the inline TOC
        const tocLinks = inlineTOC.querySelectorAll('a[href^="#"]');
        this.buildNavFromLinks(tocLinks);

        // Hide the inline TOC since we're showing it in the sidebar
        inlineTOC.style.display = 'none';
      }

      buildNavFromLinks(links) {
        if (!links.length) return;

        links.forEach((link, index) => {
          const href = link.getAttribute('href');
          const text = link.textContent.trim();
          const targetId = href.replace('#', '');
          const targetElement = document.getElementById(targetId);

          if (targetElement) {
            this.sections.push({
              id: targetId,
              element: targetElement,
            });

            // Create desktop navigation item
            const li = document.createElement('li');
            const navLink = document.createElement('a');
            navLink.href = href;
            navLink.textContent = text;
            navLink.className = 'button button-bracks font-mobile-ui-14 font-desktop-ui-14';
            navLink.dataset.targetId = targetId;

            navLink.addEventListener('click', (e) => this.handleNavClick(e, targetId));

            li.appendChild(navLink);
            this.nav.appendChild(li);
            this.navItems.push(navLink);

            // Create mobile navigation item (disclosure style)
            if (this.mobileNav) {
              const mobileLink = document.createElement('div');
              mobileLink.className = 'disclosure disclosure--large font-desktop-ui-14 font-mobile-ui-14';
              if (index > 0) {
                mobileLink.classList.add('disclosure--has-border');
              }

              const anchor = document.createElement('a');
              anchor.className = 'disclosure__label disclosure__link';
              anchor.href = href;
              anchor.textContent = text;
              anchor.addEventListener('click', (e) => this.handleNavClick(e, targetId));

              mobileLink.appendChild(anchor);
              this.mobileNav.appendChild(mobileLink);
            }
          }
        });
      }

      getHeaderOffset() {
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
        const spacing = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--spacing-2')) || 16;
        return headerHeight + spacing;
      }

      scrollToElement(element) {
        const offset = this.getHeaderOffset();
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;

        // Prevent header from hiding during programmatic scroll
        document.body.classList.add('is-programmatic-scroll');

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        // Force header to show using interval to override header-banner.js scroll behavior
        const showHeader = () => {
          const headerBanner = document.querySelector('header-banner');
          if (headerBanner) {
            headerBanner.classList.remove('header-banner--is-collapsed');
            document.body.classList.add('header-banner-is-visible');
          }
        };

        // Keep forcing header visible every 50ms for 1.5 seconds
        const interval = setInterval(showHeader, 50);
        setTimeout(() => {
          clearInterval(interval);
          showHeader();
          document.body.classList.remove('is-programmatic-scroll');
        }, 1500);
      }

      handleNavClick(e, targetId) {
        e.preventDefault();

        const targetElement = document.getElementById(targetId);
        if (!targetElement) return;

        this.isScrolling = true;

        // Update active state immediately
        this.setActiveNav(targetId);

        // Smooth scroll to target with header offset
        this.scrollToElement(targetElement);

        // Update URL hash
        history.pushState(null, '', `#${targetId}`);

        // Reset scrolling flag after animation completes
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 1000);
      }

      setActiveNav(activeId) {
        this.navItems.forEach((item) => {
          if (item.dataset.targetId === activeId) {
            item.classList.add('button-bracks--is-active');
          } else {
            item.classList.remove('button-bracks--is-active');
          }
        });
      }

      setupScrollObserver() {
        if (!this.sections.length) return;

        // Use Intersection Observer to track which section is visible
        const observerOptions = {
          root: null,
          rootMargin: '-20% 0px -70% 0px',
          threshold: 0,
        };

        const observer = new IntersectionObserver((entries) => {
          if (this.isScrolling) return;

          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const targetId = entry.target.id;
              this.setActiveNav(targetId);

              // Update URL hash without triggering scroll
              if (history.replaceState) {
                history.replaceState(null, '', `#${targetId}`);
              }
            }
          });
        }, observerOptions);

        this.sections.forEach((section) => {
          observer.observe(section.element);
        });
      }

      handleInitialHash() {
        const hash = window.location.hash;
        if (hash) {
          const targetId = hash.replace('#', '');
          const targetElement = document.getElementById(targetId);

          if (targetElement) {
            // Set active nav state
            this.setActiveNav(targetId);

            // Scroll to element after a brief delay to ensure page is loaded
            setTimeout(() => {
              this.scrollToElement(targetElement);
            }, 100);
          }
        } else if (this.sections.length > 0) {
          // Set first section as active if no hash
          this.setActiveNav(this.sections[0].id);
        }
      }
    },
  );
}
